import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getPanelRole } from '../../../../services/api';
import {
    assignNutritionPlanToPatient,
    getPatientNutritionPlans,
    searchNutritionPlansPage
} from '../../../../services/nutrition';

const AssignPatientNutritionDialog = ({
    open,
    onClose,
    patientUuid,
    patientName,
    onAssigned
}) => {
    const { t } = useTranslation();
    const label = (key, values) => t(`userPage.overview.assignments.${key}`, values);
    const [selected, setSelected] = useState(null);
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [start, setStart] = useState(dayjs().format('YYYY-MM-DD'));
    const [end, setEnd] = useState(dayjs().add(1, 'month').format('YYYY-MM-DD'));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const panelRole = getPanelRole();

    useEffect(() => {
        const timeout = setTimeout(() => setQuery(search.trim()), 300);
        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        if (open) return;
        setSelected(null);
        setSearch('');
        setQuery('');
        setError('');
        setStart(dayjs().format('YYYY-MM-DD'));
        setEnd(dayjs().add(1, 'month').format('YYYY-MM-DD'));
    }, [open, patientUuid]);

    const plans = useInfiniteQuery({
        queryKey: ['nutrition-assignment-templates', panelRole, query],
        queryFn: ({ pageParam, signal }) =>
            searchNutritionPlansPage(query, pageParam, signal),
        initialPageParam: null,
        getNextPageParam: (last, pages) => {
            const count = Number(last.headers?.['pagination-count']);
            const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
            if (Number.isFinite(count) && loaded >= count) return undefined;
            return last.headers?.['pagination-token'] || undefined;
        },
        enabled: open,
        staleTime: 60_000
    });
    const options = [
        ...new Map(
            (plans.data?.pages.flatMap((page) => page.data) || []).map((plan) => [
                plan.uuid,
                plan
            ])
        ).values()
    ];
    if (selected && !options.some((plan) => plan.uuid === selected.uuid))
        options.unshift(selected);
    const datesValid =
        /^\d{4}-\d{2}-\d{2}$/.test(start) &&
        /^\d{4}-\d{2}-\d{2}$/.test(end) &&
        dayjs(start).isValid() &&
        dayjs(end).isValid() &&
        !dayjs(end).isBefore(dayjs(start), 'day');
    const close = () => {
        if (!saving) onClose();
    };

    const assign = async () => {
        if (!selected || !datesValid || saving) return;
        setSaving(true);
        setError('');
        try {
            const previous = await getPatientNutritionPlans(patientUuid);
            const ti = dayjs(start).startOf('day');
            const tf = dayjs(end).endOf('day');
            const overlaps = previous.some(
                (plan) =>
                    !dayjs(plan.end_date).isBefore(ti) &&
                    !dayjs(plan.start_date).isAfter(tf)
            );
            if (overlaps) {
                setError('nutritionDateConflict');
                return;
            }
            await assignNutritionPlanToPatient(
                selected.uuid,
                patientUuid,
                ti.toISOString(),
                tf.toISOString()
            );
            // The API can silently skip a conflicting assignment; verify a new assignment exists.
            const updated = await getPatientNutritionPlans(patientUuid);
            const previousIds = new Set(previous.map((plan) => plan.uuid));
            const created = updated.some(
                (plan) =>
                    !previousIds.has(plan.uuid) &&
                    plan.name === selected.name &&
                    dayjs(plan.start_date).valueOf() === ti.valueOf() &&
                    dayjs(plan.end_date).valueOf() === tf.valueOf()
            );
            if (!created) {
                setError('nutritionNotConfirmed');
                return;
            }
            await onAssigned(updated);
            onClose();
        } catch {
            setError('assignNutritionError');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
            <DialogTitle>{label('nutritionDialogTitle')}</DialogTitle>
            <DialogContent>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {label('nutritionDialogDescription', { name: patientName })}
                </Typography>
                {plans.isError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {label('loadPlansError')}
                        <Button onClick={() => plans.refetch()}>{label('retry')}</Button>
                    </Alert>
                )}
                <Autocomplete
                    options={options}
                    value={selected}
                    inputValue={search}
                    onInputChange={(_, value) => setSearch(value)}
                    onChange={(_, value) => {
                        setSelected(value);
                        setError('');
                    }}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                    filterOptions={(items) => items}
                    loading={plans.isFetching}
                    disabled={saving}
                    noOptionsText={label('noPlans')}
                    loadingText={label('loading')}
                    renderInput={(params) => (
                        <TextField {...params} label={label('selectPlan')} />
                    )}
                />
                {plans.hasNextPage && (
                    <Box sx={{ mt: 1 }}>
                        <Button
                            onClick={() => plans.fetchNextPage()}
                            disabled={plans.isFetching}
                        >
                            {label('loadMorePlans')}
                        </Button>
                    </Box>
                )}
                {selected?.description && (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                        {selected.description}
                    </Typography>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                    <TextField
                        type="date"
                        fullWidth
                        label={label('startDate')}
                        value={start}
                        onChange={(event) => {
                            setStart(event.target.value);
                            setError('');
                        }}
                        disabled={saving}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        type="date"
                        fullWidth
                        label={label('endDate')}
                        value={end}
                        onChange={(event) => {
                            setEnd(event.target.value);
                            setError('');
                        }}
                        disabled={saving}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: start }}
                        error={Boolean(start && end && !datesValid)}
                        helperText={
                            start && end && !datesValid
                                ? label('invalidNutritionDates')
                                : ''
                        }
                    />
                </Stack>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {label(error)}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={close} disabled={saving}>
                    {label('cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={assign}
                    disabled={!selected || !datesValid || saving}
                    startIcon={
                        saving ? <CircularProgress color="inherit" size={16} /> : null
                    }
                >
                    {label(saving ? 'assigningPlan' : 'assignPlan')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AssignPatientNutritionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    patientUuid: PropTypes.string.isRequired,
    patientName: PropTypes.string,
    onAssigned: PropTypes.func.isRequired
};

export default AssignPatientNutritionDialog;
